import { Howl, Howler } from "howler";
import {
  SoundEffect,
  SOUND_EFFECT_PATHS,
  PRELOAD_SOUNDS,
  VolumeSettings,
  DEFAULT_VOLUME_SETTINGS,
  PlayOptions,
} from "./types";

/**
 * AudioManager - Singleton service for managing all game audio
 *
 * Handles sound effects, voice prompts, and background music with:
 * - Preloading for frequently used sounds
 * - Global mute control
 * - Volume management by category
 * - Mobile autoplay restriction handling
 */
class AudioManagerClass {
  private sounds: Map<string, Howl> = new Map();
  private currentMusic: Howl | null = null;
  private currentPrompt: Howl | null = null;
  private volumeSettings: VolumeSettings = { ...DEFAULT_VOLUME_SETTINGS };
  private isMuted: boolean = false;
  private isInitialized: boolean = false;
  private pendingPreloads: Set<string> = new Set();

  /**
   * Initialize the audio system and preload essential sounds
   * Should be called once on app start, ideally after user interaction
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    // Unlock audio context on mobile devices
    await this.unlockAudioContext();

    // Preload frequently used sounds
    await this.preloadSounds(PRELOAD_SOUNDS);

    this.isInitialized = true;
  }

  /**
   * Unlock audio context for mobile browsers
   * Mobile Safari and Chrome require user interaction before playing audio
   */
  private async unlockAudioContext(): Promise<void> {
    return new Promise((resolve) => {
      // Howler handles audio context unlocking internally
      // We just need to ensure the context is created
      if (Howler.ctx?.state === "suspended") {
        Howler.ctx.resume().then(() => resolve());
      } else {
        resolve();
      }
    });
  }

  /**
   * Preload a list of sound effects for instant playback
   */
  async preloadSounds(effects: SoundEffect[]): Promise<void> {
    const loadPromises = effects.map((effect) =>
      this.loadSound(SOUND_EFFECT_PATHS[effect])
    );
    await Promise.all(loadPromises);
  }

  /**
   * Preload sounds for a specific discipline
   * Call this when entering a discipline to load all related audio
   */
  async preloadDiscipline(slug: string): Promise<void> {
    // Load discipline-specific prompt directory
    // In production, this would load from a manifest or API
    const basePath = `/audio/prompts/${slug}`;
    // For now, just mark as preloaded - actual loading happens on demand
    this.pendingPreloads.add(basePath);
  }

  /**
   * Load a sound file and cache it
   */
  private loadSound(path: string): Promise<Howl> {
    return new Promise((resolve, reject) => {
      // Return cached sound if already loaded
      const existing = this.sounds.get(path);
      if (existing) {
        resolve(existing);
        return;
      }

      const sound = new Howl({
        src: [path],
        preload: true,
        onload: () => {
          this.sounds.set(path, sound);
          resolve(sound);
        },
        onloaderror: (_id, error) => {
          console.warn(`Failed to load audio: ${path}`, error);
          // Resolve anyway to not block app initialization
          resolve(sound);
        },
      });
    });
  }

  /**
   * Play a sound effect
   */
  playEffect(effect: SoundEffect, options?: PlayOptions): void {
    if (this.isMuted) return;

    const path = SOUND_EFFECT_PATHS[effect];
    const sound = this.sounds.get(path);

    if (sound) {
      const volume =
        (options?.volume ?? 1) *
        this.volumeSettings.effects *
        this.volumeSettings.master;
      sound.volume(volume);
      sound.rate(options?.rate ?? 1);
      sound.loop(options?.loop ?? false);
      sound.play();
    } else {
      // Load and play on demand if not preloaded
      this.loadSound(path).then((loadedSound) => {
        if (!this.isMuted) {
          const volume =
            (options?.volume ?? 1) *
            this.volumeSettings.effects *
            this.volumeSettings.master;
          loadedSound.volume(volume);
          loadedSound.rate(options?.rate ?? 1);
          loadedSound.loop(options?.loop ?? false);
          loadedSound.play();
        }
      });
    }
  }

  /**
   * Play a voice prompt (instruction audio)
   * Stops any currently playing prompt first
   */
  async playPrompt(filename: string): Promise<void> {
    if (this.isMuted) return;

    // Stop current prompt if playing
    if (this.currentPrompt) {
      this.currentPrompt.stop();
    }

    return new Promise((resolve) => {
      const prompt = new Howl({
        src: [filename],
        volume: this.volumeSettings.prompts * this.volumeSettings.master,
        onend: () => {
          this.currentPrompt = null;
          resolve();
        },
        onloaderror: () => {
          console.warn(`Failed to load prompt: ${filename}`);
          this.currentPrompt = null;
          resolve();
        },
      });

      this.currentPrompt = prompt;
      prompt.play();
    });
  }

  /**
   * Play background music (looping)
   * Crossfades from any currently playing music
   */
  playMusic(track: string): void {
    if (this.isMuted) return;

    // Fade out current music
    if (this.currentMusic) {
      const oldMusic = this.currentMusic;
      oldMusic.fade(
        oldMusic.volume(),
        0,
        500 // 500ms fade out
      );
      setTimeout(() => {
        oldMusic.stop();
        oldMusic.unload();
      }, 500);
    }

    const music = new Howl({
      src: [track],
      loop: true,
      volume: 0,
      onload: () => {
        music.play();
        music.fade(0, this.volumeSettings.music * this.volumeSettings.master, 500);
      },
      onloaderror: () => {
        console.warn(`Failed to load music: ${track}`);
      },
    });

    this.currentMusic = music;
  }

  /**
   * Stop background music with fade out
   */
  stopMusic(): void {
    if (this.currentMusic) {
      const music = this.currentMusic;
      music.fade(music.volume(), 0, 500);
      setTimeout(() => {
        music.stop();
        music.unload();
        if (this.currentMusic === music) {
          this.currentMusic = null;
        }
      }, 500);
    }
  }

  /**
   * Stop all audio playback
   */
  stopAll(): void {
    // Stop all sound effects
    this.sounds.forEach((sound) => sound.stop());

    // Stop prompt
    if (this.currentPrompt) {
      this.currentPrompt.stop();
      this.currentPrompt = null;
    }

    // Stop music
    if (this.currentMusic) {
      this.currentMusic.stop();
      this.currentMusic.unload();
      this.currentMusic = null;
    }
  }

  /**
   * Set master volume level
   */
  setVolume(level: number): void {
    this.volumeSettings.master = Math.max(0, Math.min(1, level));
    Howler.volume(this.volumeSettings.master);
  }

  /**
   * Set volume for a specific category
   */
  setCategoryVolume(
    category: keyof Omit<VolumeSettings, "master">,
    level: number
  ): void {
    this.volumeSettings[category] = Math.max(0, Math.min(1, level));

    // Update currently playing music volume if music category changed
    if (category === "music" && this.currentMusic) {
      this.currentMusic.volume(
        this.volumeSettings.music * this.volumeSettings.master
      );
    }
  }

  /**
   * Set muted state
   */
  setMuted(muted: boolean): void {
    this.isMuted = muted;
    Howler.mute(muted);

    if (muted) {
      // Stop prompts when muting
      if (this.currentPrompt) {
        this.currentPrompt.stop();
        this.currentPrompt = null;
      }
    }
  }

  /**
   * Toggle mute state
   */
  toggleMute(): boolean {
    this.setMuted(!this.isMuted);
    return this.isMuted;
  }

  /**
   * Get current mute state
   */
  getMuted(): boolean {
    return this.isMuted;
  }

  /**
   * Get current volume settings
   */
  getVolumeSettings(): VolumeSettings {
    return { ...this.volumeSettings };
  }

  /**
   * Check if audio system is initialized
   */
  getIsInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Clean up and unload all audio resources
   * Call when unmounting the app or switching contexts
   */
  dispose(): void {
    this.stopAll();
    this.sounds.forEach((sound) => sound.unload());
    this.sounds.clear();
    this.isInitialized = false;
  }
}

// Export singleton instance
export const AudioManager = new AudioManagerClass();
